'use client';

import { Study } from '@/lib/study';
import { T } from '@/lib/tokens';
import { Nav, StepHead, StepWrap } from '../StepShell';

type Props = {
  data: Study;
  setData: (d: Study) => void;
  onBack: () => void;
  onNext: () => void;
};

export function Step6Review({ data, setData, onBack, onNext }: Props) {
  const qs = data.questions;

  const update = (i: number, text: string) => {
    const arr = [...qs];
    arr[i] = { ...arr[i], text };
    setData({ ...data, questions: arr });
  };

  const remove = (i: number) => {
    const arr = [...qs];
    arr.splice(i, 1);
    setData({ ...data, questions: arr });
  };

  const add = () => {
    const maxId = qs.reduce((m, q) => Math.max(m, q.id), 0);
    setData({ ...data, questions: [...qs, { id: maxId + 1, text: '' }] });
  };

  const move = (i: number, dir: -1 | 1) => {
    const arr = [...qs];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setData({ ...data, questions: arr });
  };

  return (
    <StepWrap>
      <StepHead
        label="Step 7 — Questions"
        title="Review and refine"
        subtitle="Edit the questions, reorder them, remove ones that don't fit, or add new ones. The AI interviewer will ask these in order, but may add follow-up probes."
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {qs.map((q, i) => (
          <div
            key={q.id}
            style={{
              background: T.white,
              border: `1.5px solid ${T.creamBorder}`,
              borderRadius: 12,
              padding: '16px 18px',
              display: 'flex',
              gap: 14,
              alignItems: 'flex-start',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 4, flexShrink: 0 }}>
              <button
                onClick={() => move(i, -1)}
                disabled={i === 0}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: i === 0 ? 'default' : 'pointer',
                  color: i === 0 ? T.creamBorder : T.espressoLight,
                  fontSize: 14,
                  padding: '2px 4px',
                  lineHeight: 1,
                }}
                title="Move up"
              >
                ▲
              </button>
              <button
                onClick={() => move(i, 1)}
                disabled={i === qs.length - 1}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: i === qs.length - 1 ? 'default' : 'pointer',
                  color: i === qs.length - 1 ? T.creamBorder : T.espressoLight,
                  fontSize: 14,
                  padding: '2px 4px',
                  lineHeight: 1,
                }}
                title="Move down"
              >
                ▼
              </button>
            </div>

            <span style={{ color: T.terra, fontWeight: 700, fontSize: 15, minWidth: 24, paddingTop: 10 }}>{i + 1}</span>

            <textarea
              value={q.text}
              onChange={(e) => update(i, e.target.value)}
              placeholder="Enter your question…"
              rows={2}
              style={{
                flex: 1,
                resize: 'vertical',
                border: `1px solid ${T.creamBorder}`,
                borderRadius: 8,
                padding: '8px 12px',
                fontFamily: 'inherit',
                fontSize: 15,
                lineHeight: 1.55,
                color: T.espresso,
                background: T.cream,
                outline: 'none',
              }}
              onFocus={(e) => (e.target.style.borderColor = T.terra)}
              onBlur={(e) => (e.target.style.borderColor = T.creamBorder)}
            />

            <button
              onClick={() => remove(i)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: T.espressoLight,
                fontSize: 20,
                padding: '4px 4px',
                lineHeight: 1,
                flexShrink: 0,
                paddingTop: 8,
              }}
              title="Remove"
            >
              ×
            </button>
          </div>
        ))}

        <button
          onClick={add}
          style={{
            border: `1.5px dashed ${T.creamBorder}`,
            borderRadius: 12,
            padding: '14px 18px',
            background: 'none',
            cursor: 'pointer',
            color: T.espressoLight,
            fontSize: 14,
            fontFamily: 'inherit',
          }}
        >
          + Add a question
        </button>
      </div>

      <Nav
        onBack={onBack}
        onNext={onNext}
        nextDisabled={qs.length === 0 || qs.some((q) => !q.text.trim())}
      />
    </StepWrap>
  );
}
